import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { OtpInput, type OtpInputHandle } from '@/features/auth/components/OtpInput';
import { TOKEN_LENGTH } from '@/features/auth/useLoginFlow';
import { Button } from '@/shared/ui/Button';

interface TokenStepProps {
  email: string;
  submitting: boolean;
  error: string | null;
  /** Resolve `false` quando o código não passou — é o sinal de limpar o campo. */
  onSubmit: (token: string) => Promise<boolean>;
  onResend: () => Promise<boolean>;
  onBack: () => void;
  onChange: () => void;
}

export function TokenStep({ email, submitting, error, onSubmit, onResend, onBack, onChange }: TokenStepProps) {
  const [token, setToken] = useState('');
  const [resent, setResent] = useState(false);
  const otpRef = useRef<OtpInputHandle>(null);

  // Pedido de foco contado, em vez de `focusFirst()` chamado na hora: quando a
  // tentativa termina as caixas ainda estão desabilitadas na tela (`submitting` só cai
  // no render seguinte), e `focus()` em input desabilitado não faz nada. O efeito roda
  // depois do render, com o campo já vazio e habilitado.
  const [focusRequest, setFocusRequest] = useState(0);
  const requestFocus = useCallback(() => setFocusRequest(current => current + 1), []);

  useEffect(() => {
    if (focusRequest > 0) otpRef.current?.focusFirst();
  }, [focusRequest]);

  const handleChange = (value: string) => {
    setToken(value);
    setResent(false);
    onChange();
  };

  // Código recusado esvazia as caixas e devolve o foco à primeira: o operador digita o
  // próximo direto, sem apagar seis caixas, e o código que já falhou não fica no campo
  // para ser reenviado por um clique no botão. É também o que faz o envio automático
  // valer de novo — ele dispara na transição para completo (ver `OtpInput`).
  //
  // Em caso de sucesso não há o que limpar: `signIn` troca a tela.
  const attempt = useCallback(
    async (value: string) => {
      const ok = await onSubmit(value);
      if (ok) return;
      setToken('');
      requestFocus();
    },
    [onSubmit, requestFocus],
  );

  // Submete sozinho ao completar os 6 dígitos — o clique no botão fica opcional.
  const handleComplete = useCallback(
    (value: string) => {
      if (!submitting) void attempt(value);
    },
    [submitting, attempt],
  );

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void attempt(token);
  };

  const handleResend = async () => {
    setToken('');
    const ok = await onResend();
    setResent(ok);
    requestFocus();
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-6">
      {/* Esta etapa mora no mesmo cartão da anterior, pintado com a cor do evento:
          o texto usa `on-brand`, que é escuro sobre cartão claro e claro sobre
          cartão escuro. O título acompanha o tamanho do nome do evento na etapa do
          e-mail — ele não é o título da tela, é o cabeçalho de um passo. */}
      <header className="space-y-1.5">
        <h1 className="text-base font-semibold text-on-brand">Confirme o código</h1>
        <p className="text-[0.8125rem] text-on-brand/75">
          Enviamos um código de {TOKEN_LENGTH} dígitos para <span className="font-medium text-on-brand">{email}</span>.
        </p>
      </header>

      <div className="space-y-2">
        <OtpInput
          ref={otpRef}
          length={TOKEN_LENGTH}
          value={token}
          onChange={handleChange}
          onComplete={handleComplete}
          disabled={submitting}
          invalid={Boolean(error)}
          label="Código de acesso"
        />

        {error ? (
          <p role="alert" className="text-[0.8125rem] font-medium text-rejected">
            {error}
          </p>
        ) : resent ? (
          <p role="status" className="text-[0.8125rem] font-medium text-approved">
            Novo código enviado.
          </p>
        ) : (
          <p className="text-[0.8125rem] text-on-brand/75">O código expira em alguns minutos.</p>
        )}
      </div>

      <Button type="submit" variant="gradient" size="lg" fullWidth loading={submitting} disabled={token.length !== TOKEN_LENGTH}>
        Acessar painel
      </Button>

      <div className="flex items-center justify-between text-[0.8125rem]">
        <button
          type="button"
          onClick={onBack}
          disabled={submitting}
          className="font-medium text-on-brand/75 transition-colors hover:text-on-brand disabled:opacity-50"
        >
          Trocar e-mail
        </button>
        <button
          type="button"
          onClick={handleResend}
          disabled={submitting}
          className="font-medium text-on-brand underline decoration-on-brand/40 underline-offset-4 transition-colors hover:decoration-on-brand disabled:opacity-50"
        >
          Reenviar código
        </button>
      </div>
    </form>
  );
}
